'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FaArrowRight, FaPause, FaPlay } from 'react-icons/fa'
import AppStoreLink from '../components/AppStoreLink'
import { AUDIENCES, audienceStoreUrl } from './audiences'

/**
 * AudienceTour — animated slideshow of the eight themed App Store custom
 * product page decks, one slide per audience. Follows the same carousel
 * discipline as the homepage AppTour: labelled tabs (never anonymous dots),
 * viewport-gated autoplay, hover/focus pause, reduced-motion respect, a
 * real pause control (WCAG 2.2.2), and a permanent stop the moment the
 * user picks a slide themselves.
 *
 * Deck artwork lives at /public/for/<slug>-deck.jpg — the actual lead slide
 * of each ASC custom product page, so the site preview and the store page a
 * visitor lands on are the same campaign. Images link out with ?ppid=.
 */

const AUTO_ADVANCE_MS = 5000

export default function AudienceTour() {
  const prefersReducedMotion = useReducedMotion()
  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [inView, setInView] = useState(false)
  const [hovered, setHovered] = useState(false)
  const userTookOverRef = useRef(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const shouldAutoPlay = playing && inView && !hovered && !prefersReducedMotion

  useEffect(() => {
    if (!shouldAutoPlay) return
    const t = setInterval(() => {
      setActive((i) => (i + 1) % AUDIENCES.length)
    }, AUTO_ADVANCE_MS)
    return () => clearInterval(t)
  }, [shouldAutoPlay])

  const select = useCallback((i: number) => {
    userTookOverRef.current = true
    setPlaying(false)
    setActive(i)
  }, [])

  const slide = AUDIENCES[active]

  return (
    <section
      ref={sectionRef}
      className="w-full bg-deepForest py-14 sm:py-16 md:py-24 overflow-hidden"
      aria-labelledby="audience-tour-heading"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="frame">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-goldLight mb-3">
            On the App Store
          </p>
          <h2
            id="audience-tour-heading"
            className="font-display text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-cream leading-tight mb-3"
          >
            Eight store pages, one for your routine
          </h2>
          <p className="text-base sm:text-lg text-cream/70 leading-relaxed">
            Each audience gets its own themed App Store page. Tap a deck to
            see yours.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:gap-14 items-center">
          {/* ── Tab list (desktop: vertical rail; mobile: scrollable chips) ── */}
          <div>
            {/* Mobile chips */}
            <div
              className="md:hidden flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-4"
              role="tablist"
              aria-label="Audience store pages"
            >
              {AUDIENCES.map((a, i) => (
                <button
                  key={a.slug}
                  role="tab"
                  aria-selected={i === active}
                  onClick={() => select(i)}
                  className={`shrink-0 px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight ${
                    i === active
                      ? 'bg-goldLight text-deepForest'
                      : 'bg-white/[0.07] text-cream/70 hover:text-cream'
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            {/* Mobile caption for the active slide */}
            <div className="md:hidden text-center mb-6" aria-live="polite">
              <h3 className="text-lg font-bold text-cream mb-1">{slide.label}</h3>
              <p className="text-sm text-cream/70 leading-relaxed">{slide.hook}</p>
            </div>

            {/* Desktop rail */}
            <div className="hidden md:block" role="tablist" aria-label="Audience store pages">
              {AUDIENCES.map((a, i) => {
                const isActive = i === active
                return (
                  <button
                    key={a.slug}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => select(i)}
                    className={`group relative w-full text-left rounded-2xl px-5 py-3.5 mb-1.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight ${
                      isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                    } pressable-sm`}
                  >
                    <span className="flex items-baseline gap-3">
                      <span
                        className={`font-display text-sm font-bold tabular-nums transition-colors duration-200 ${
                          isActive ? 'text-goldLight' : 'text-cream/35'
                        }`}
                        aria-hidden="true"
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">
                        <span className={`block font-bold text-base transition-colors duration-200 ${isActive ? 'text-cream' : 'text-cream/60 group-hover:text-cream/85'}`}>
                          {a.label}
                        </span>
                        <AnimatePresence initial={false}>
                          {isActive && (
                            <motion.span
                              className="block text-sm text-cream/65 leading-relaxed overflow-hidden"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1, marginTop: 4 }}
                              exit={{ height: 0, opacity: 0, marginTop: 0 }}
                              transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
                            >
                              {a.hook}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </span>
                    </span>
                    {isActive && shouldAutoPlay && (
                      <span className="absolute left-5 right-5 bottom-1 h-px bg-white/10 overflow-hidden" aria-hidden="true">
                        <motion.span
                          key={`${active}-progress`}
                          className="absolute inset-y-0 left-0 bg-goldLight/70"
                          initial={{ width: '0%' }}
                          animate={{ width: '100%' }}
                          transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
                        />
                      </span>
                    )}
                  </button>
                )
              })}

              <button
                type="button"
                onClick={() => {
                  userTookOverRef.current = true
                  setPlaying((p) => !p)
                }}
                className="mt-3 ml-5 inline-flex items-center gap-2 text-xs font-semibold text-cream/50 hover:text-cream transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight rounded-lg px-2 py-1.5"
                aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
              >
                {playing ? <FaPause className="w-2.5 h-2.5" aria-hidden="true" /> : <FaPlay className="w-2.5 h-2.5" aria-hidden="true" />}
                {playing ? 'Pause' : 'Play'}
              </button>
            </div>
          </div>

          {/* ── Deck slide — store artwork crossfades in one stable frame ── */}
          <div className="flex flex-col items-center md:items-end gap-4">
            <AppStoreLink
              source="for_hub_audience_tour"
              href={audienceStoreUrl(slide.ppid)}
              trackingParams={{ page_type: 'for_hub', slide: slide.slug }}
              aria-label={`Open the ${slide.label} App Store page`}
              className="block group"
            >
              <div className="relative w-[240px] sm:w-[270px] md:w-[290px] aspect-[1320/2868] rounded-[1.6rem] shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden transition-transform duration-300 group-hoverable:scale-[1.015] ring-1 ring-white/10">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={slide.slug}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.45, ease: 'easeOut' }}
                  >
                    <Image
                      src={`/for/${slide.slug}-deck.jpg`}
                      alt={`${slide.label} App Store page deck`}
                      width={1000}
                      height={2173}
                      sizes="(max-width: 640px) 240px, 290px"
                      className="w-full h-auto"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </AppStoreLink>
            <Link
              href={`/for/${slide.slug}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-goldLight hover:text-cream transition-colors duration-200"
            >
              More for {slide.label}
              <FaArrowRight className="w-3 h-3" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Preload every deck so crossfades never flash an unloaded image. */}
        <div className="hidden" aria-hidden="true">
          {AUDIENCES.map((a) => (
            <Image key={a.slug} src={`/for/${a.slug}-deck.jpg`} alt="" width={1000} height={2173} loading="eager" />
          ))}
        </div>
      </div>
    </section>
  )
}
